import { X, CheckCircle2, AlertTriangle, Clock, Calendar, Users, FileSpreadsheet } from 'lucide-react';

/**
 * Displays rich analytics after importing a Timesheet PDF.
 * Supports multi-employee results. Skips rendering when no payload.
 */
const Stat = ({ label, value, hint, tone = 'default' }) => {
  const tones = {
    default: 'bg-white border-gray-200',
    good: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    warn: 'bg-amber-50 border-amber-200 text-amber-800',
    bad: 'bg-rose-50 border-rose-200 text-rose-800',
    info: 'bg-sky-50 border-sky-200 text-sky-800',
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-semibold leading-tight mt-0.5">{value}</div>
      {hint && <div className="text-[11px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
};

const EmployeeReport = ({ r }) => {
  const s = r.summary || {};
  const matched = r.status === 'matched';
  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/40 space-y-4" data-testid="employee-report">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="font-semibold text-gray-800">{r.employee_name}</span>
            {r.department && <span className="text-xs text-gray-500">• {r.department}</span>}
          </div>
          {matched ? (
            <div className="text-xs text-emerald-700 flex items-center gap-1 mt-1">
              <CheckCircle2 className="w-3 h-3" /> Padan: {r.matched_staff_name} ({r.inserted} baru, {r.updated} kemaskini)
            </div>
          ) : (
            <div className="text-xs text-rose-700 flex items-center gap-1 mt-1">
              <AlertTriangle className="w-3 h-3" /> {r.message || 'Staf tidak dijumpai'}
            </div>
          )}
        </div>
      </div>

      {/* Summary grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <Stat label="Total Hari" value={s.total_days || 0} tone="info" />
        <Stat label="On Time" value={s.on_time || 0} tone="good" hint={`${s.on_time_pct || 0}%`} />
        <Stat label="Lewat" value={s.late || 0} tone={s.late ? 'warn' : 'default'} hint="> 9:30 AM" />
        <Stat label="Balik Awal" value={s.early_out || 0} tone={s.early_out ? 'warn' : 'default'} />
        <Stat label="Jumlah Jam" value={`${s.total_hours || 0}h`} hint={`Purata ${s.avg_hours_per_day || 0}h/hari`} />
        <Stat label="Auto Clock Out" value={s.auto_clock_out || 0} tone={s.auto_clock_out ? 'info' : 'default'} hint="Perlu semakan" />
      </div>

      {/* Weekly breakdown */}
      {r.weekly?.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
            <Calendar className="w-4 h-4" /> Breakdown Mingguan
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Minggu</th>
                  <th className="px-3 py-2 text-left">Tempoh</th>
                  <th className="px-3 py-2 text-center">Hari</th>
                  <th className="px-3 py-2 text-center">Jam</th>
                  <th className="px-3 py-2 text-center">Lewat</th>
                  <th className="px-3 py-2 text-center">Awal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {r.weekly.map((w) => (
                  <tr key={w.week} data-testid={`week-row-${w.week}`}>
                    <td className="px-3 py-2 font-medium">{w.week}</td>
                    <td className="px-3 py-2 text-gray-600">{w.period}</td>
                    <td className="px-3 py-2 text-center">{w.days}</td>
                    <td className="px-3 py-2 text-center">{w.hours}h</td>
                    <td className={`px-3 py-2 text-center ${w.late ? 'text-amber-700 font-semibold' : ''}`}>{w.late}</td>
                    <td className={`px-3 py-2 text-center ${w.early ? 'text-amber-700 font-semibold' : ''}`}>{w.early}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Problems */}
      {r.problems?.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Tarikh Perlu Perhatian ({r.problems.length})
          </div>
          <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
            {r.problems.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 text-xs" data-testid="problem-row">
                <span className="font-medium text-gray-700">{p.date}</span>
                <span className={p.severity === 'warning' ? 'text-amber-700' : 'text-gray-600'}>{p.issue}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TimesheetImportResult = ({ result, onClose }) => {
  if (!result) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-start sm:justify-center z-50 sm:p-4 overflow-y-auto" data-testid="timesheet-import-modal">
      <div className="bg-white w-full sm:rounded-2xl rounded-t-2xl shadow-2xl sm:max-w-5xl sm:my-8 overflow-hidden h-[94vh] sm:h-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-white">
          <div className="min-w-0">
            <h2 className="text-base sm:text-xl font-bold text-gray-800 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-red-700 shrink-0" />
              <span className="truncate">Analisis Kehadiran Bulanan</span>
            </h2>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 truncate">{result.message}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 shrink-0"
            data-testid="close-import-result-btn"
            aria-label="Tutup"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Top summary */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <Stat label="Staf Dijumpai" value={result.employees_found || 0} tone="info" />
            <Stat label="Rekod Baru" value={result.imported || 0} tone="good" />
            <Stat label="Dikemaskini" value={result.updated || 0} tone="default" />
            <Stat label="Tidak Padan" value={result.total_errors || 0} tone={result.total_errors ? 'bad' : 'default'} />
          </div>
        </div>

        {/* Per-employee reports */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
          {result.results?.map((r, i) => <EmployeeReport key={i} r={r} />)}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
          <div className="text-[11px] sm:text-xs text-gray-500 flex items-center gap-2 min-w-0">
            <Clock className="w-3 h-3 shrink-0" /> <span className="truncate">Data disimpan dalam modul Kehadiran</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 min-h-[44px] bg-red-700 text-white rounded-lg hover:bg-red-800 text-sm font-medium shrink-0"
            data-testid="confirm-import-result-btn"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimesheetImportResult;
